// Curator tools inside the llama player — admins and vetted magicians only. Colour the whole song, set start points
// (one starred favourite plays first), bin the unlistenable, sort into crates, and draw at random from the repositories.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dices, MapPin, Trash2, Star, X, Undo2, Archive } from "lucide-react";
import { API } from "../App";
import { usePlayer } from "./PlayerContext";
import { ax, COLORS, clock, errText } from "./sampler";

const MAX_CUES = 12;
const plain = (cues) => cues.map(({ at, starred }) => ({ at, starred }));

// The full mark (colour, start points, crates, bin) for the song that's playing.
export const useCuratorMark = (p) => {
  const t = p?.current;
  const key = p?.curate && t?.skey ? t.skey : null;
  const [mark, setMark] = useState(null);
  useEffect(() => {
    setMark(null);
    if (!key) return undefined;
    if (t.reel) { setMark({ color: t.color, cues: t.cues || [], crates: t.crates || [], binned: !!t.binned }); return undefined; }
    let alive = true;
    axios.get(`${API}/sampler/item`, { ...ax, params: { key } }).then(({ data }) => alive && setMark(data)).catch(() => {});
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useCallback(async (patch) => {
    try {
      const { data } = await axios.put(`${API}/sampler/marks`, { key, ...patch }, ax);
      setMark(data);
      p.applyMark(t, data);
      return data;
    } catch (e) { toast.error(errText(e, "Couldn't save that")); return null; }
  }, [key, t, p]);
  const addCue = (at) => {
    const cues = plain(mark?.cues || []);
    if (cues.length >= MAX_CUES) { toast.error(`Up to ${MAX_CUES} start points per song`); return; }
    save({ cues: [...cues, { at, starred: !cues.some((c) => c.starred) }] }).then((m) => m && toast.success(`Start point at ${clock(at)}`));
  };
  return { mark, save, addCue };
};

const usePools = () => {
  const [crates, setCrates] = useState([]);
  const [sources, setSources] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/sampler/crates`, ax).then(({ data }) => setCrates(data)).catch(() => {});
    axios.get(`${API}/sampler/sources`, ax).then(({ data }) => setSources(data.filter((s) => s.status === "ready"))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  return { crates, sources, reloadCrates: load };
};

const SEL = "min-w-0 rounded-full bg-white/10 border border-white/15 px-2 py-1 text-[11px] font-bold text-white focus:outline-none focus:border-amber-300/60";

const DrawRow = ({ pools }) => {
  const p = usePlayer();
  const [pool, setPool] = useState(p.reel?.crate ? `crate:${p.reel.crate}` : p.reel?.source || "imports");
  const filters = pool.startsWith("crate:") ? { source: "all", crate: pool.slice(6) } : { source: pool };
  return (
    <div className="flex items-center gap-1.5">
      <select value={pool} onChange={(e) => setPool(e.target.value)} className={`${SEL} flex-1`} aria-label="Draw from" data-testid="curate-pool">
        <option value="imports">Imported crates · Aadam Jacobs + imports</option>
        <option value="all">Everything on the site</option>
        <optgroup label="Repositories">
          {pools.sources.map((s) => <option key={s.source_id} value={s.source_id}>{s.hidden ? "Private repository" : s.name}</option>)}
        </optgroup>
        {pools.crates.length > 0 && (
          <optgroup label="Crates">
            {pools.crates.map((c) => <option key={c.crate_id} value={`crate:${c.crate_id}`}>{c.hidden ? "Private crate" : c.name} ({c.count})</option>)}
          </optgroup>
        )}
      </select>
      <button type="button" onClick={() => p.drawReel(filters)} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-400 text-amber-950 px-2.5 py-1 text-[11px] font-black hover:bg-amber-300" data-testid="curate-draw-btn"><Dices className="h-3.5 w-3.5" /> Draw</button>
      {p.reel && <button type="button" onClick={p.exitReel} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] font-bold hover:bg-white/20" title="Back to the HOARD player" data-testid="curate-exit-reel"><Undo2 className="h-3.5 w-3.5" /> HOARD</button>}
    </div>
  );
};

const CrateSelect = ({ mark, save, pools }) => {
  const choose = async (v) => {
    if (!v) return;
    if (v === "__new") {
      const name = window.prompt("Name the new crate");
      if (!name?.trim()) return;
      try {
        const { data } = await axios.post(`${API}/sampler/crates`, { name: name.trim() }, ax);
        if (await save({ crate_add: data.crate_id })) toast.success(`In the "${data.name}" crate`);
        pools.reloadCrates();
      } catch (e) { toast.error(errText(e, "Couldn't make that crate")); }
      return;
    }
    const inIt = (mark?.crates || []).includes(v);
    if (await save(inIt ? { crate_remove: v } : { crate_add: v })) pools.reloadCrates();
  };
  return (
    <select value="" onChange={(e) => choose(e.target.value)} className={`${SEL} max-w-[110px]`} aria-label="Crates" data-testid="curate-crate-select">
      <option value="">{mark?.crates?.length ? `In ${mark.crates.length} crate${mark.crates.length > 1 ? "s" : ""}` : "Crate…"}</option>
      {pools.crates.map((c) => <option key={c.crate_id} value={c.crate_id}>{(mark?.crates || []).includes(c.crate_id) ? "✓ " : ""}{c.name}</option>)}
      <option value="__new">+ New crate</option>
    </select>
  );
};

const CueChips = ({ cm }) => {
  const p = usePlayer();
  const cues = cm.mark?.cues || [];
  if (!cues.length) return <p className="m-0 text-[10px] text-white/45">No start points yet — tap the pin, then tap the waveform.</p>;
  const star = (i) => cm.save({ cues: plain(cues).map((c, j) => ({ ...c, starred: j === i })) });
  const drop = (i) => {
    const rest = plain(cues).filter((_, j) => j !== i);
    if (cues[i].starred && rest.length && !rest.some((c) => c.starred)) rest[0].starred = true;
    cm.save({ cues: rest });
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid="curate-cues">
      {cues.map((c, i) => (
        <span key={c.cue_id || i} className={`inline-flex items-center rounded-full text-[10px] font-bold ${c.starred ? "bg-white text-[#120A24]" : "bg-white/10"}`} data-testid={`curate-cue-${i}`}>
          <button type="button" onClick={() => star(i)} className="pl-1.5 pr-0.5 py-0.5" title={c.starred ? "Favourite — plays first" : "Make this the favourite"} data-testid={`curate-cue-star-${i}`}><Star className={`h-3 w-3 ${c.starred ? "fill-current" : ""}`} /></button>
          <button type="button" onClick={() => p.seek(c.at)} className="px-0.5 py-0.5 tabular-nums" data-testid={`curate-cue-jump-${i}`}>{clock(c.at)}</button>
          <button type="button" onClick={() => drop(i)} className="pr-1.5 pl-0.5 py-0.5 opacity-60 hover:opacity-100" aria-label="Remove start point" data-testid={`curate-cue-remove-${i}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
};

export const CuratorPanel = ({ cm, pinMode, setPinMode }) => {
  const p = usePlayer();
  const pools = usePools();
  const t = p.current;
  const { mark, save } = cm;
  const setColor = async (c) => {
    const m = await save({ color: mark?.color === c ? "" : c });
    if (m && !t.reel && mark?.color === "red" && m.color !== "red") p.reloadLibrary();
    if (m?.color === "red") toast("Red — hidden from the public player", { description: "Only you and vetted magicians can hear it until age-lock is on." });
  };
  const bin = async () => {
    if (!(await save({ binned: true }))) return;
    toast("In the Sound Bin", { description: "It won't play again. Restore it any time from Crates & imports." });
    p.next();
  };
  return (
    <div className="mx-3 mb-2 rounded-xl bg-white/[0.06] ring-1 ring-amber-300/25 p-2 space-y-2" data-testid="curator-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">Curate</span>
        <Link to="/sampler" className="inline-flex items-center gap-1 text-[10px] font-bold text-[#54E3FF] hover:underline" data-testid="curate-open-crates"><Archive className="h-3 w-3" /> Crates &amp; imports</Link>
      </div>
      <DrawRow pools={pools} />
      <div className="flex items-center gap-1.5 flex-wrap">
        {Object.entries(COLORS).map(([c, v]) => (
          <button key={c} type="button" onClick={() => setColor(c)} disabled={!mark} title={`${v.label} — ${v.hint}`}
            className={`h-7 w-7 rounded-full text-[11px] font-black text-[#120A24] transition-transform disabled:opacity-40 ${mark?.color === c ? "ring-2 ring-white scale-110" : "opacity-70 hover:opacity-100"}`}
            style={{ backgroundColor: v.hex }} aria-pressed={mark?.color === c} data-testid={`curate-color-${c}`}>{v.label[0]}</button>
        ))}
        <button type="button" onClick={() => setPinMode(!pinMode)} disabled={!mark} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold disabled:opacity-40 ${pinMode ? "bg-white text-[#120A24]" : "bg-white/10 hover:bg-white/20"}`} title="Tap the waveform to add start points" data-testid="curate-pin-toggle"><MapPin className="h-3.5 w-3.5" /> {pinMode ? "Tap waveform" : "Start pts"}</button>
        <CrateSelect mark={mark} save={save} pools={pools} />
        <button type="button" onClick={bin} disabled={!mark} className="ml-auto inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-rose-500/40 px-2 py-1 text-[11px] font-bold disabled:opacity-40" title="Sound Bin — never plays again, never deleted" data-testid="curate-bin-btn"><Trash2 className="h-3.5 w-3.5" /> Bin</button>
      </div>
      <CueChips cm={cm} />
    </div>
  );
};
